import { getEnv } from "@bfc-software/bfc-web-ui/environment";

/**
 * The products an organisation can open from the picker.
 *
 * `tiles()` is the one place that decides which appear. Which tools an
 * organisation may open is a real access decision that isn't designed yet -
 * `AuthUtils` carries `hasPermission` (role/permission) and `getFeatureFlag`
 * (per-org flags), but nothing calls them and no role means "BFC staff". Until
 * that lands, availability comes from an env flag (`internalToolsEnabled` in
 * app-toolbox); `TileContext` is the seam, and role-based availability means
 * adding the fields it needs and changing one predicate in `tiles()`.
 *
 * Titles and copy live in the portal translations, keyed by id - the design
 * treats them as content, and they should not be duplicated here.
 */

/** Products this app serves itself, each behind a route in its own tree. */
export type InternalTileId = "outbound" | "analytics" | "deliver" | "internal";

/** Products hosted elsewhere. The picker links out to these in a new tab. */
export type ExternalTileId = "loadBuilder";

export type TileId = InternalTileId | ExternalTileId;

/**
 * Where the picker sends the user. A lookup rather than a ternary, `as const
 * satisfies` so `Link` still checks each value against the route tree.
 */
export const TILE_ROUTES = {
  outbound: "/$lang/orgs/$org/outbound",
  analytics: "/$lang/orgs/$org/analytics",
  deliver: "/$lang/orgs/$org/deliver",
  internal: "/$lang/orgs/$org/internal",
} as const satisfies Record<InternalTileId, string>;

export type TileDestination =
  /** A route in this app's tree; the picker renders a router `Link` with `{ lang, org }`. */
  | { kind: "internal"; to: (typeof TILE_ROUTES)[InternalTileId] }
  /** Another application; the picker renders a plain new-tab anchor. `href` is fully resolved. */
  | { kind: "external"; href: string };

/**
 * A tile that is shown. Visibility is not a field: a tile that should not appear
 * is absent from `tiles()`'s result, so "available but coming soon" and
 * "unavailable but open" cannot be written.
 */
export type Tile = {
  id: TileId;
  /** Ready to use, versus visible but not yet built out. */
  status: "open" | "coming-soon";
  destination: TileDestination;
};

/** Who's asking: addresses and authorizes the request an external tile makes, and is the seam for role-based availability. */
export type TileContext = {
  orgSlug: string;
  orgId: string;
  token: string;
};

/** The products the signed-in user can see in the picker, in display order. */
export function tiles(ctx: TileContext): ReadonlyArray<Tile> {
  // Read per call, never at import: the deployment target is read from the
  // environment when asked, and a value captured at module load would pin
  // whichever target happened to be set first.
  const env = getEnv();
  return [
    internalTile("outbound", "coming-soon"),
    externalTile(
      "loadBuilder",
      "open",
      resolveExternalUrl(env.loadBuilderUrl, ctx),
    ),
    internalTile("deliver", "open"),
    internalTile("analytics", "coming-soon"),
    ...(env.internalToolsEnabled ? [internalTile("internal", "open")] : []),
  ];
}

function internalTile(id: InternalTileId, status: Tile["status"]): Tile {
  return { id, status, destination: { kind: "internal", to: TILE_ROUTES[id] } };
}

function externalTile(
  id: ExternalTileId,
  status: Tile["status"],
  href: string,
): Tile {
  return { id, status, destination: { kind: "external", href } };
}

/**
 * `#customer#` is a per-environment placeholder - only production's host is
 * customer-scoped - so it is replaced when present and left alone otherwise.
 * The org and the signed-in session travel as query params on every target so
 * the destination can identify and authorize the request either way.
 */
function resolveExternalUrl(
  template: string,
  { orgSlug, orgId, token }: TileContext,
): string {
  const url = new URL(template.replace("#customer#", orgSlug));
  url.searchParams.set("org_id", orgId);
  url.searchParams.set("tkn", token);
  return url.toString();
}
